import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { labelGroupCaption } from '../labelVocab';
import { focusOwnsKey, isTypingTarget } from '../keyboard';
import { inlineKeys } from '../keys';
import { useKeys } from '../keyScope';
import { useMedia, PHONE } from '../media';
import { hasOpenSurface, useDismiss } from '../useDismiss';
import { useQuery } from '@tanstack/react-query';
import {
  listTasks,
  getGoalTree,
  listProjects,
  type Task,
  type TaskFilter,
  type TaskStatus,
  type GoalTreeNode,
  listLabels,
  listLabelGroups,
  getTaskOverview,
  taskOverviewKey,
} from '../api';
import { goalChipsFor } from '../goalFocus';
import { useAccountDay } from '../accountDay';
import { endOfWeek } from '../timezones';
import { readableInstant } from '../dateLabels';
import { goalColorMap, resolveTaskColor, nearestColored } from '../colors';
import { TaskMenu, useTaskMenu } from './TaskMenu';
import { TaskOverview, tileIsPressed } from './TaskOverview';
import { Capture } from './Capture';
import { TaskBoard } from './TaskBoard';
import { TaskStatusControl, TaskProgressMark, type ReadbackClaim } from './TaskStatusControl';
import { PriorityMark } from './StatusBadge';
import { dueLook, dueClass, PRIORITY_LABELS, dueTitle } from '../taskVocab';
import { TaskCommandBar, useTaskCommand, COMMANDS } from './TaskCommands';
import { soleSelectedTask, useCursorFollowsOpenTask, useTaskSelection } from '../useTaskSelection';
import { QueryState } from './QueryState';
import { goalOptions, renderGoalOption } from './pickerOptions';
import { Select } from './Select';
import { MultiSelect } from './MultiSelect';
import { Icon } from './Icon';
import { ParentLink } from './ParentLink';
import { SubtaskPill } from './SubtaskPill';
import { BlockedByPill, blockersAreTheContent } from './BlockedByPill';
import { ChecklistLine } from './ChecklistLine';
import { AssigneeChip } from './AssigneeChip';
import { WorkElsewhere } from './WorkElsewhere';
import { useInProgressCount } from './WipNotice';
import { ReadbackSlot, useCommandReadback } from './CommandReadback';
import { useAssignees } from '../useAssignees';

/**
 * The floor under the age pill's threshold — not the threshold itself.
 *
 * A fortnight, matching the "slowing" band goals use. That was a consistency argument and it was
 * the whole rule, and the measurement says a fixed floor cannot be: *Nd old* appeared on **58 of
 * 61** rows on one account and on 20 of 22 on another, where twenty of the twenty-two read the
 * same `20d old`. A threshold everything passes separates nothing, and the pill had become the row's
 * only metadata on most rows — one persona read it as the list "quietly grading me for not having
 * finished them".
 *
 * So the floor stays and the rule is `staleThreshold` below: relative to the list on screen. A
 * fortnight is still the earliest an age is worth a word, whatever the list looks like.
 */
const STALE_DAYS = 14;

const ageInDays = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

/**
 * How old a row has to be, on *this* list, before its age says something.
 *
 * Twice the median, floored at `STALE_DAYS`. The point of the pill is to mark the outlier, and
 * "outlier" is a claim about the rows beside it: a 20-day task among twenty others at 20 days is
 * ordinary, and the same task among three at five days is the thing you have been avoiding. Both
 * accounts in the finding check out — the minimalist's twenty-two rows at 19–20 days give a median
 * of 20 and a threshold of 40, so none of them show it.
 *
 * The cost, and it is a real one: an account with one 60-day task among ten at 50 days shows no
 * pill at all, because there is no outlier there — eleven old tasks is a state of the list rather
 * than a fact about one row. The answer for "which is the oldest" is the *Oldest* sort, which still
 * ranks every row, and the `title` on the pill still carries the capture date. If a persona round
 * finds somebody hunting for the oldest by eye and failing, that is the signal to revisit this, not
 * a reason to lower the floor.
 */
export function staleThreshold(createdAts: readonly string[]): number {
  if (createdAts.length === 0) return STALE_DAYS;
  const ages = createdAts.map(ageInDays).sort((a, b) => a - b);
  const mid = Math.floor(ages.length / 2);
  /* The even case takes the mean of the two middles rather than the upper one. On a short list the
     upper-middle rule biases high — [10, 10, 30, 30] would read 30 and threshold at 60 instead of
     20 and 40 — which hides the pill on the rows it is for. `staleThreshold.spec.ts` pins that
     pair. */
  const median = ages.length % 2 === 1 ? ages[mid]! : Math.round((ages[mid - 1]! + ages[mid]!) / 2);
  return Math.max(STALE_DAYS, 2 * median);
}

/** Every node in the tree, flat — the goals themselves, for colour and titles. */
function flattenGoalNodes(nodes: GoalTreeNode[]): GoalTreeNode[] {
  return nodes.flatMap((n) => [n, ...flattenGoalNodes(n.children)]);
}

function TaskRow({
  task,
  onOpen,
  goalChips,
  labelNames,
  color,
  isCursor,
  isSelected,
  selecting,
  onFocusRow,
  onToggleSelect,
  onExtendSelect,
  onAct,
  onFail,
  onMenu,
  myUserId,
  nameFor,
  canClaim,
  nested = false,
  expanded = false,
  onToggleExpand,
  blockersAreContent = false,
  ageThreshold = STALE_DAYS,
}: {
  task: Task;
  /**
   * How old this row must be for its age to be worth a pill — see `staleThreshold`.
   *
   * Passed in rather than computed here, because it is a fact about the whole list and a row
   * cannot see its neighbours. Defaulted to the floor so a caller that has not worked it out yet
   * gets the old behaviour rather than no pill at all.
   */
  ageThreshold?: number;
  /**
   * Whether this screen is *about* blocked work, which is what un-caps the blocker pill.
   *
   * Passed down rather than read from a context or re-derived here, because the condition is a
   * fact about the list's filter and the row has no other business knowing one. See
   * `BlockedByPill`.
   */
  blockersAreContent?: boolean;
  /** Drawn one step in, under the parent it belongs to. */
  nested?: boolean;
  expanded?: boolean;
  /** Absent on a row with nothing to expand, and in the flat view where nothing nests. */
  onToggleExpand?: () => void;
  color: { colorClass: string | null; from: string };
  onOpen: (id: string) => void;
  /** Id to title, so a row can name what it serves without another fetch. */
  goalChips: ReadonlyMap<string, { title: string; colorClass: string }>;
  /** Id to name, same reason. */
  labelNames: ReadonlyMap<string, string>;
  isCursor: boolean;
  isSelected: boolean;
  /** While on, a plain tap selects instead of opening — see the title's `onClick`. */
  selecting: boolean;
  onFocusRow: () => void;
  onToggleSelect: () => void;
  onExtendSelect: () => void;
  /**
   * This row is about to be acted on by something the user pointed at.
   *
   * The list uses it to park the cursor if the row goes — see `holdGap` — and to say what
   * happened, with the way back. The row itself does not know whether the action removes it, and
   * does not need to: arming is undone by the next id list if the row is still in it.
   *
   * It takes the status because the row's own controls now do what `x` and `3` do, and those have
   * always left a readback. A box that completes in one press and says nothing would be the only
   * path to `completeTask` with no undo attached to it.
   */
  onAct: (status: TaskStatus) => ReadbackClaim;
  /**
   * The change `onAct` announced was refused by the server. `onAct` runs before the request, so
   * the readback it put up has to be withdrawn when the request fails — see `onFail` on the box.
   */
  onFail: (claim: ReadbackClaim) => void;
  onMenu: (event: React.MouseEvent, task: Task) => void;
  /** Who the viewer is, so their own name reads as "you". */
  myUserId: string | null;
  nameFor: (userId: string | null) => string | null;
  canClaim: (projectId: string | null) => boolean;
}) {
  /* The pill's verdict is about the account's calendar, not the laptop's — see `useAccountDay`. */
  const { timezone, today } = useAccountDay();
  return (
    <li
      className={[
        'task-row',
        nested ? 'is-nested' : '',
        color.colorClass ?? '',
        task.isBlocked ? 'is-blocked' : '',
        isCursor ? 'is-cursor' : '',
        isSelected ? 'is-selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-cursor={isCursor || undefined}
      /* Where the task panel hands focus back when it closes — see `taskOpener`. */
      data-task-id={task.id}
      onContextMenu={(e) => onMenu(e, task)}
      title={color.colorClass ? `Colour from ${color.from}` : undefined}
      onMouseDown={(e) => {
        // Shift-click extends, ctrl/cmd-click toggles one, a plain click just moves the cursor —
        // so pointer and keyboard drive the same selection rather than two competing notions of
        // "current".
        //
        // A modifier click is handled wherever it lands, including on the title. This used to bail
        // on any `button`, and `.task-title` is a button spanning 739 of a 920px row — so shift-
        // and ctrl-click did nothing across four fifths of every row, and on the bare inbox rows
        // that carry no trailing pills there was no reachable target at all. Measured across 56
        // rows: a median 134px strip worked and 13 rows offered none.
        //
        // Plain clicks still belong to whatever control they hit; only the modifiers are
        // intercepted, because a modifier click on a task means "select", never "activate".
        //
        // The bail-out for a plain click on a control went with the same change, and that half was
        // the reported data loss: opening a task by the one gesture everybody uses left the cursor
        // on whatever row it was last on, and the next `x` completed *that* row. Moving the cursor
        // takes nothing away from the control — nothing is prevented and the click proceeds — so
        // the row you touched is the row you are on whatever you touched on it: the title, the
        // checkbox, the caret, or a right-click for the menu.
        const modified = e.shiftKey || e.metaKey || e.ctrlKey;
        // Text fields keep their own modifier behaviour (extend a text selection).
        if (modified && (e.target as HTMLElement).closest('input, select, textarea')) return;
        if (e.shiftKey) {
          e.preventDefault();
          onExtendSelect();
        } else if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          onToggleSelect();
        } else {
          onFocusRow();
        }
      }}
    >
      {/*
        The only control left on the row.

        `start` and `move…` used to sit here — 137 pixels of every 920-pixel row, against 158 for
        the title, which made the title the fourth thing in reading order. Both moved to the
        command bar, which acts on the cursor. The checkbox stays because completing is the most
        frequent transition by a wide margin and a checkbox says "finished" in a way no menu does.
      */}
      {/*
        The checkbox completes — in one press, which is the thing it did not do.

        A note stood here saying I had briefly made this a selection box and that repurposing it
        would retrain a reflex. That half was right and still is: on a device with `Space`,
        shift-click and ctrl-click, selecting has three answers and the box is not one of them.
        What the note did not say is that on a phone none of those three exist, which is why Select
        mode exists at all — and in that mode the box was still completing the reflex's *other*
        job, so the manager tapped one at 375 to gather a batch and started a task instead. So the
        box selects **while the mode that has no other way to select is on**, and completes at
        every other moment. See `TaskStatusControl`.

        `Start` came back beside it as its own glyph rather than as the box's first press. Two
        controls, but one is a checkbox and one is a button, which is not the pair that was removed
        here — that was a checkbox, a `start` button *and* a `move…` select, 137 pixels of a
        920-pixel row against 158 for the title. This one is a 24px mark in a column of its own at
        the row's right-hand end — see `.progress-slot` below.
      */}
      {/*
        The way in, on the rows that have one.

        A fixed-width slot on every row rather than only the ones that expand: without it the
        checkbox column shifts by the width of a caret depending on whether the task happens to
        have subtasks, and a list whose left edge moves per row is harder to scan than one with a
        column of empty space in it.
      */}
      <span className="expander-slot">
        {onToggleExpand && (
          <button
            className="link expander"
            onClick={onToggleExpand}
            aria-expanded={expanded}
            aria-label={
              expanded
                ? `Hide the subtasks of ${task.title}`
                : `Show the ${task.subtasks.total} subtasks of ${task.title}`
            }
          >
            <Icon name="caret" size={12} />
          </button>
        )}
      </span>
      <TaskStatusControl
        task={task}
        onAct={onAct}
        onFail={onFail}
        selection={selecting ? { selected: isSelected, onToggle: onToggleSelect } : undefined}
        modifierSelects
      />
      {/*
        Title and meta in one box, so the meta can sit *under* the title rather than beside it.

        `display: contents` above 40rem, so the desktop row is byte-identical: the two children go
        on being flex items of `.task-row` exactly as before. Below it the box becomes the flex
        container and the meta wraps onto its own line starting at the title's left edge — which is
        Today's shortlist row, the format this app already chose for a phone (`.shortlist-main`).

        The alternative was a `padding-left` on the meta equal to the expander slot plus the state
        box plus two gaps. Same picture, computed by hand from three other rules, and wrong the
        first time any of them changes.
      */}
      <div className="task-main">
        {/*
        The title and its parent travel together.

        Both are the answer to "what is this" — "Book the sparky, part of Rewire the workshop" —
        and the row is a flex line whose title has to keep a zero basis, or a long one wraps and
        pushes the checkbox onto a line of its own. A zero basis also makes it *grow*, which threw
        the parent link against the right margin where it read as one more trailing attribute in a
        column of its own. So the group takes the growth and the title sits at its content width
        inside it.
      */}
        <span className="task-title-group">
          <button
            className="link task-title"
            // A modifier click was a selection gesture on mousedown above; `preventDefault` there
            // does not stop the click, so without this the same press would both extend the
            // selection and open the task.
            onClick={(e) => {
              if (e.shiftKey || e.metaKey || e.ctrlKey) return;
              /*
              In selection mode a tap picks the row rather than opening it.

              On a phone there was no way into the command bar at all: the keyboard hint is not
              rendered, tapping a row body selects nothing, and shift-click needs a keyboard the
              device does not have — so bulk triage, which is the manager's whole workflow, was
              desktop-only. A mode rather than a long-press, because a long-press is invisible
              until someone tells you about it, and the thing this list most needed was fewer
              gestures nobody could find.
            */
              if (selecting) {
                onToggleSelect();
                return;
              }
              onOpen(task.id);
            }}
          >
            {task.title}
          </button>
          {/*
          Not when it is drawn under the row that names it.

          The whole point of the arrow is to answer "why is this here" on a surface where the
          parent is nowhere in sight. Directly beneath its parent it repeats the row above, on
          every child, which is the noise nesting exists to remove.
        */}
          {task.parent && !nested && <ParentLink parent={task.parent} onOpen={onOpen} />}
        </span>
        {/*
          Where the task had got to, under the title rather than in the meta strip.

          `.task-meta`'s drop order decides what falls off first at narrow widths, and this is the
          one thing on the row that must not — it is the reason to start. See `ChecklistLine`.
        */}
        <ChecklistLine task={task} />
        <span className="task-meta">
          <AssigneeChip
            task={task}
            myUserId={myUserId}
            name={nameFor(task.assigneeUserId)}
            actionable={canClaim(task.projectId)}
          />
          <SubtaskPill task={task} />
          {/*
          Icons, not words, for the closed sets that repeat on every row — each with a `title` for
          hover and an accessible name for everything that is not a mouse. See `Icon.tsx` for the
          three tests a label had to pass to earn one.
        */}
          {/*
          What it is blocked by, not merely that it is stuck. The row carried a hexagon and a
          dimmed title, which says a task is stuck and nothing about what would unstick it — and
          the app has had the answer in hand since the blocked state was computed.

          Shared with the board card now rather than drawn here: see `BlockedByPill`, which also
          carries why `blockersAreTheContent` is a condition and not the default.
        */}
          <BlockedByPill task={task} full={blockersAreContent} />
          <PriorityMark priority={task.priority} />
          {/*
          One pill, four states. A deadline a week gone used to render in exactly the same grey as
          one a fortnight away, so nothing in a 56-row list stood out — the Today shortlist already
          distinguished them and this, the screen for working through volume, did not.
        */}
          {(() => {
            const look = dueLook(task, today, timezone);
            const age = ageInDays(task.createdAt);
            /*
              The age, before the deadline and only when it is the row's outlier.

              Two changes to one pill, both from the same measurement. It moved ahead of the due
              pill so lateness keeps the rightmost slot — the `.task-meta` drop order means the
              last chip is the one that survives a narrow row, and a grey capture age surviving
              where a red deadline does not is the wrong way round. And it is suppressed outright
              on a row already showing `Nd late`: the manager measured *19d late* beside *19d old*
              on the same row with half the pairs within a day of each other, which is one fact
              wearing two pills.

              `tone === 'bad'` is the red deadline pill specifically. A passed *plan* is quiet-toned
              and keeps its age pill, because `planned 20 Jul` and "captured 40 days ago" are two
              different facts rather than the same one twice — and the median rule above is what
              keeps that pair off the rows where it would not mean anything.
            */
            const showAge = age >= ageThreshold && look?.tone !== 'bad';
            return (
              <>
                {showAge && (
                  <span
                    className="pill"
                    title={`Captured ${readableInstant(task.createdAt, timezone, 'day')}`}
                  >
                    {age}d old
                  </span>
                )}
                {look ? (
                  <span className={dueClass(look)} title={dueTitle(look)}>
                    {look.text}
                  </span>
                ) : null}
              </>
            );
          })()}
          {/*
          Quantities stay as text: a number is already the shortest form of itself. They sit after
          the date because a deadline outranks an estimate when the row runs out of room — see the
          slot and drop order beside `.task-meta` in the stylesheet.
        */}
          {task.estimateMinutes && <span className="pill est-chip">{task.estimateMinutes}m</span>}
          {task.complexityPoints && (
            <span className="pill points-chip">{task.complexityPoints} pts</span>
          )}
          {/*
          That it repeats, in the same words a template says it in.

          The row said nothing about this at all, so a recurring task and a one-off looked
          identical and the first thing a person learned was a second task arriving after they
          finished one — while `get_task` had been handing an agent the rule all along. The
          sentence and not the rule: `recurrenceText` is what `describeRule` makes of the RRULE,
          and it is what `Templates.tsx` already draws on a template's card.
        */}
          {task.recurrenceText && <span className="pill">{task.recurrenceText}</span>}
          {/* Open vocabularies — a goal or project name has no fixed set, so it cannot be an icon.
            This is exactly why the goal chip carries a colour *and* the name. */}
          {/*
          The first goal, then a count — the same treatment the label chip below already had.
          A task serving two goals printed one of them and no marker, so the row silently
          disagreed with the detail panel, which lists both.
        */}
          {(() => {
            const chips = goalChipsFor(task, goalChips);
            const rest = chips.slice(1);
            return (
              <>
                {chips.slice(0, 1).map((chip) => (
                  <span
                    key={chip.title}
                    className={`pill goal-chip ${chip.colorClass}`}
                    title={`Goal: ${chip.title}`}
                  >
                    {chip.title}
                  </span>
                ))}
                {rest.length > 0 && (
                  <span
                    className="pill goal-chip"
                    title={`Also: ${rest.map((c) => c.title).join(', ')}`}
                  >
                    +{rest.length}
                  </span>
                )}
              </>
            );
          })()}
          {/*
          The project chip went with the screen it linked to, and this comment claimed labels
          carried it — which was an intention, not a fact. Nothing on a row showed a label, so a
          label could be set, filtered on and searched by, and never once seen while scanning.

          Two, then a count. Deliberately plainer than the goal chip: the goal is *why* the task
          exists and earns its colour, a label is what kind of thing it is and only needs naming.
          Colouring both would put two competing colour signals on one row, and the row's own band
          may already be the label's colour.
        */}
          {task.labelIds
            .map((id) => labelNames.get(id))
            .filter((name): name is string => name !== undefined)
            .slice(0, 2)
            .map((name) => (
              <span key={name} className="pill label-chip" title={`Label: ${name}`}>
                {name}
              </span>
            ))}
          {task.labelIds.length > 2 && (
            <span className="pill label-chip" title="Open the task to see them all">
              +{task.labelIds.length - 2}
            </span>
          )}
        </span>
      </div>
      {/*
        Whether the work has started — the offer while it has not, the fact once it has — in a
        column of its own at the row's right-hand end.

        It sat on the title's line, straight after the words, because the meta strip below 40rem
        is a line of its own and a 24px control there took a bare row from 43px to 67. That
        measurement still holds and this placement still honours it: a sibling of `.task-main`
        rather than a child, so on a phone it sits beside the two-line block and adds no line. What
        the old placement cost was the column — the mark followed the title's length, landing at
        x=277 on one row and x=329 on the next, and the minimalist persona could not find it twice
        in the same place (`01a15a31-174b`). The slot is 24px whether it holds anything or not, so
        the column holds on a done row too.

        The state it moves to travels with the offer, so the two are one slot: press ▶ and the same
        glyph, in the same place, stops being a button and becomes the filled mark. `displayStatus`,
        inside the mark, is what decides that "next, but blocked by something" shows as blocked.
      */}
      <span className="progress-slot">
        <TaskProgressMark task={task} onAct={onAct} onFail={onFail} />
      </span>
    </li>
  );
}

/**
 * Which of the two views the Tasks screen is showing, remembered. See `SCREEN_KEY` in `App.tsx`
 * for the same idea.
 *
 * This was exported so the `?` panel could read it: the list and the board bind different keys,
 * and a panel showing the list's set while the board was on screen documented keys that do
 * nothing. `App` read it out of `localStorage` mid-render to guess. The guess is gone — whichever
 * of the two is listening claims its own scope through `useKeys`, so the panel asks the keyboard
 * rather than the storage — and the constant is private again.
 */
const VIEW_KEY = 'planner.tasksView';
/**
 * The scope choice, remembered per **account** — unlike the screen, the view and the sort.
 *
 * Those three are preferences about how a person likes to look at a list, and sharing them between
 * two accounts in one browser is harmless. A scope can name a project, and a project belongs to an
 * account: kept per browser, a work account that left Tasks on one of its projects opened the next
 * account's Tasks on that same id, which the server answered with an empty list while Today listed
 * all of the second account's work (01a15879-0a72). So the key carries the account id.
 *
 * Namespaced rather than cleared when the signed-in user changes, because the case this came from
 * is one person moving between their own two accounts: clearing would hand each of them `mine`
 * after every switch, and would need a "who was here last" key of its own to know when to fire.
 * `readScope` still checks the project against the account's own list, which namespacing alone
 * cannot do — a project can be left, deleted or archived under a remembered id.
 */
const SCOPE_KEY = 'planner.tasksScope';
const scopeKey = (accountId: string) => `${SCOPE_KEY}:${accountId}`;
/*
  The sort choice, remembered like the other two.

  The scope switcher and the list/board toggle both survive a reload and this did not — and it is
  the control that answers "what is on fire". Measured: the default order did not contain the worst
  item in its first 22 rows; sorting by due date put it first; a reload put it back. Three controls
  on one line, and the only one that forgot was the one worth remembering.
*/
const SORT_KEY = 'planner.tasksSort';

/**
 * What a list means (§5).
 *
 * `mine` is `assignee_user_id = me`, whatever project it is in, and it is the default — which is
 * what makes "my list is mine" true by construction rather than by remembering to filter. `all`
 * is deliberately available and deliberately not the default: it is what the screen did before
 * scope existed, and what it did before is the bug.
 */
type Scope = { kind: 'mine' } | { kind: 'all' } | { kind: 'project'; projectId: string };

/**
 * The remembered scope, as written. Whether a remembered project is still this account's is
 * answered in `TaskList`, once the project list has loaded.
 *
 * The un-namespaced key from before 1.69.1 is adopted once by whichever account reads it first and
 * then removed: it cannot say whose it was, and the reachability check sends a foreign project
 * back to `mine`, so the worst case is one account forgetting its scope once.
 *
 * This only reads. The removal happens in `TaskList`'s write effect, once the adopted value has
 * been written under the account's own key: `readScope` runs as a `useState` initializer, which
 * React may call more than once (StrictMode does), and a removal here was correct only because
 * React 19 happens to keep the first call's result.
 */
const readScope = (accountId: string): Scope => {
  const saved = localStorage.getItem(scopeKey(accountId)) ?? localStorage.getItem(SCOPE_KEY);
  if (saved === 'all') return { kind: 'all' };
  if (saved?.startsWith('project:')) return { kind: 'project', projectId: saved.slice(8) };
  return { kind: 'mine' };
};

const writeScope = (accountId: string, scope: Scope) =>
  localStorage.setItem(
    scopeKey(accountId),
    scope.kind === 'project' ? `project:${scope.projectId}` : scope.kind,
  );

/**
 * Aim this screen at one project's board before it mounts.
 *
 * Today's pointer sends somebody here from a screen where this component is not mounted, so there
 * is no state to set — but both controls already read their initial value out of `localStorage`
 * on mount for exactly this reason (a refresh should not be a navigation), and writing the two
 * keys is what "arrive on the board of that project" means. Exported rather than open-coded in
 * `App` so the key names stay owned by the screen that reads them.
 */
export function aimAtProjectBoard(accountId: string, projectId: string) {
  writeScope(accountId, { kind: 'project', projectId });
  localStorage.setItem(VIEW_KEY, 'board');
}

/**
 * One navigation's worth of "and show me the late ones", consumed on arrival.
 *
 * Not `SCOPE_KEY`'s neighbour in spirit: the two keys above are *preferences* and survive every
 * reload on purpose, while this is a single press's instruction and has to be gone by the next
 * one — otherwise the Deadline control would reset itself to Overdue every time somebody came
 * back to this screen. `readAim` below removes it as it reads it.
 */
const AIM_KEY = 'planner.tasksAim';

/**
 * What an aim can ask for. `late` is Today's band (`aimAtLate`). `parked` is the shelf — the list
 * filtered to `status: parked`, exactly what the summary's *N parked* chip and the list's foot line
 * apply on this screen — and it is the way in for a press that starts on another screen: the
 * dormancy notice (`01a1774e-1814`) writes it, with `mine` and `list` beside it as `aimAtLate`
 * does, because the job parks the caller's own work. Anything else is ignored.
 */
type TasksAim = 'late' | 'parked';

/**
 * Aim this screen at everything late before it mounts.
 *
 * Today's band sends somebody here from a screen where this component is not mounted, so there is
 * no state to set — the same situation `aimAtProjectBoard` is in, and the same answer.
 *
 * It writes the scope as well as the filter, and that part is not optional. The number the band
 * printed is `myWork(principal)` — the overview takes no scope at all — while this list sends
 * whichever scope it remembered (`all`, or one project). So arriving with a remembered `all` would
 * open a set that does not match the figure somebody just pressed, which is the mismatch
 * `01a10163-f9af` is filed about on the Tasks header's own chip. `list` for the same reason: the
 * board groups by status and the question being answered is "which ones".
 */
export function aimAtLate(accountId: string) {
  writeScope(accountId, { kind: 'mine' });
  localStorage.setItem(VIEW_KEY, 'list');
  localStorage.setItem(AIM_KEY, 'late');
}

/**
 * Aim this screen at the parked shelf before it mounts — the dormancy notice's way in.
 *
 * `mine` and `list` for `aimAtLate`'s reasons: the job parks the caller's own work, so the shelf it
 * is talking about is `mine`, and the question is "which ones". The caller is also responsible for
 * remounting this screen when it is already open, since the aim is only read on mount.
 */
export function aimAtParked(accountId: string) {
  writeScope(accountId, { kind: 'mine' });
  localStorage.setItem(VIEW_KEY, 'list');
  localStorage.setItem(AIM_KEY, 'parked');
}

/** The pending aim, taken rather than peeked at — see `AIM_KEY`. */
function readAim(): TasksAim | null {
  const aim = localStorage.getItem(AIM_KEY);
  if (aim !== null) localStorage.removeItem(AIM_KEY);
  return aim === 'late' || aim === 'parked' ? aim : null;
}

/**
 * *Parent › Child*, as a name and nothing more.
 *
 * Projects are flat here on purpose (§5.1): picking one shows its own tasks and never a child's or
 * a parent's. Two nested projects already exist, and left unadorned a child sits in the list beside
 * its own parent looking like a peer — which is functionally right and reads as a mistake. The
 * prefix costs one string and carries no behaviour.
 */
function prefixedName(
  project: { id: string; title: string; parentProjectId: string | null },
  byId: ReadonlyMap<string, { title: string; parentProjectId: string | null }>,
): string {
  /*
    The immediate parent, not the whole ancestry.

    The prefix exists to stop a child sitting beside its own parent reading as a mistake, and one
    level does that. The full chain does it too and costs 530 pixels — "SA: Home lab rebuild 2026 ›
    SA: Network segmentation › SA: VLAN cutover" — which no popup width makes readable without the
    menu becoming the screen. Ordering by path already carries the rest of the structure.
  */
  const parent = project.parentProjectId ? byId.get(project.parentProjectId) : undefined;
  return parent ? `${parent.title} › ${project.title}` : project.title;
}

export function TaskList({
  accountId,
  onOpen,
  onOpenFull,
  openTaskId,
}: {
  /** Whose list this is — the remembered scope is kept per account, see `SCOPE_KEY`. */
  accountId: string;
  onOpen: (id: string) => void;
  /** The full read view, when the caller offers one. */
  onOpenFull?: (id: string) => void;
  /**
   * Which task the app currently has open, so the cursor can follow it — see
   * `useCursorFollowsOpenTask`. Read only: this list opens tasks through `onOpen` and never
   * closes one.
   */
  openTaskId?: string | null;
}) {
  /*
    The overdue filter is a server-side predicate, and this is the day it is asked about.

    It sent `todayIso()`, the browser's day — so the control you press to answer "what is late"
    was handing the server a different calendar from the one the server grades with, and for as
    many hours as the laptop and the profile disagree it either hid a late row or invented one.
  */
  const { today } = useAccountDay();
  /* Taken once, before either control reads it: the aim decides the filter *or* the Deadline
     choice, and a second `readAim` would find the key already gone. */
  const [aim] = useState(readAim);
  const [filter, setFilter] = useState<TaskFilter>(() => {
    const saved = localStorage.getItem(SORT_KEY);
    const sort: TaskFilter =
      saved && saved !== 'default' ? { sort: saved as TaskFilter['sort'] } : {};
    return aim === 'parked' ? { ...sort, status: 'parked' } : sort;
  });
  /*
    The deadline filter is one choice on screen and three fields on the wire, because the four
    options ask three different questions: "Due today" and "Due this week" are a day the deadline
    must fall on or before, "Overdue" is `late` — a passed deadline *or* a passed plan, which no
    cutoff can express — and "No date" is `hasDue: false`, which no cutoff can express either,
    a null being before nothing. Keeping the choice itself in state means the control can show
    what was picked rather than trying to infer it back out of three fields.
  */
  /* `'overdue'` is `late`, which is the predicate Today's band counted with — so an arrival from
     there sets the choice rather than the filter, and the control shows what it is doing. */
  const [dueChoice, setDueChoice] = useState<string | null>(() =>
    aim === 'late' ? 'overdue' : null,
  );
  useEffect(() => {
    /*
      Every branch here names a *day*, and none of them computes an instant.

      They used to. "Due today" and "This week" sent `dueBefore`, and built it in the browser:
      `d.setHours(23, 59, 59, 999)` on `new Date()`. Both halves of that are the laptop's — its
      midnight and its date — so the control was wrong in two independent ways whenever the laptop
      was not sitting in the account's zone. Measured against the account in Pago Pago with the
      browser on UTC, "Due today" asked for everything before 23:59:59.999Z when the account's
      today ends at 11:00Z, and listed work due at midday *tomorrow*; with the account in
      Kiritimati the same expression fell ten hours short and hid work due that evening. At 23:30
      in Warsaw it filtered by yesterday outright, the account in Auckland already being on the
      next date.

      The cure is not better arithmetic here — the browser does not know the account's zone well
      enough to resolve a local midnight, and `dayStartInstant` is not something this bundle may
      import or copy (`timezones.ts` duplicates what suggests, never what judges). It is that the
      question was the wrong shape: "due today" is about a calendar, so the day goes over the wire
      and the server, which holds the zone, ends it. Exactly what `late` below already does, and
      what its `ListTasksFilter` note predicted for this one: `dueBefore` cannot express it.
    */
    setFilter((f) => {
      const rest = { ...f };
      delete rest.dueOnOrBefore;
      delete rest.hasDue;
      delete rest.late;
      /*
        The same lateness the rows are drawn with, not a second approximation of it.

        This was `dueBefore: now`, which is only the *deadline* half — so Overdue dropped every
        task the list beside it was grading as late on a passed plan. Seven of them on the
        manager's account, "Write the v2 migration cutover plan" among them: the control you reach
        for to answer "what is late" hid rows the screen had just called late. `late` mirrors
        `dueStatus` branch for branch, server-side.
      */
      if (dueChoice === 'overdue') return { ...rest, late: today };
      if (dueChoice === 'today') return { ...rest, dueOnOrBefore: today };
      if (dueChoice === 'week') return { ...rest, dueOnOrBefore: endOfWeek(today) };
      if (dueChoice === 'none') return { ...rest, hasDue: false };
      return rest;
    });
  }, [dueChoice, today]);
  /*
    Two views of the same filter, not two screens.

    A ninth nav tab would have made the board a separate place with its own filters to set up
    again; a replacement would have cost the list, which is still the only way to search and the
    only way to browse finished work. Sharing `filter` means the overview tiles above drive the
    board for free.
  */
  /*
    The view you were on, kept across a reload.

    Reported by the owner: "when I am on board view and refresh because I want to see if anything
    transitioned state, I get to task list view again." This was plain state, so every reload — and
    every deploy — dropped you back to the list.

    `App.tsx` already solved exactly this for the screen itself, and its comment gives the
    reasoning: a refresh should not be a navigation. The list/board choice is the same kind of
    state and never got the same treatment. Same mechanism, same storage, one key.

    Unlike the task view's mode, this is safe to remember: the toggle is on screen and shows which
    half is active, so a remembered value can never disagree with what you can see.
  */
  /*
    The flat view, on request.

    Not persisted, unlike `view`: nesting is the answer to "what is on my plate", and flat is what
    you want for one particular question — usually "where did that subtask go" — which is asked
    and then finished with. A preference that outlives the question would quietly restore the six
    overlapping rows this exists to remove.
  */
  const [showSubtasks, setShowSubtasks] = useState(false);
  /** Which parents are open. Collapsed by default; the pill says whether there is anything in. */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  const [scope, setScope] = useState<Scope>(() => readScope(accountId));
  useEffect(() => {
    writeScope(accountId, scope);
    // The 1.69.0 key has now been adopted under this account's own; see `readScope`.
    localStorage.removeItem(SCOPE_KEY);
  }, [accountId, scope]);

  const [view, setView] = useState<'list' | 'board'>(() =>
    localStorage.getItem(VIEW_KEY) === 'board' ? 'board' : 'list',
  );
  useEffect(() => {
    localStorage.setItem(VIEW_KEY, view);
  }, [view]);
  /*
    Seven filter controls, behind one button.

    They were all permanently visible, which with the twelve summary tiles above them put the first
    task row 665 pixels down a 720-pixel viewport. Search stays out — it is the one you reach for
    without having decided to filter — and the button carries a count so a filter can never be left
    on invisibly, which is the actual risk of hiding them.
  */
  const [filtersOpen, setFiltersOpen] = useState(false);
  /* See the note on `<Capture />` below: the header takes over at this width. */
  const phone = useMedia(PHONE);
  /*
    Selection mode, for a device with no keyboard.

    Only offered at phone width: everywhere else `Space`, shift-click and ctrl-click already do
    this and better, and a mode that duplicates three working gestures is a fourth thing to
    explain. Turned off when the width changes, so a rotation cannot leave the list in a state its
    controls have stopped offering.
  */
  const [selecting, setSelecting] = useState(false);
  useEffect(() => {
    if (!phone) setSelecting(false);
  }, [phone]);
  /*
    The panel and the button that opens it both count as "inside", or the dismissing pointerdown
    would close it a moment before the button's click reopened it.
  */
  const filterPanel = useRef<HTMLDivElement>(null);
  const filterButton = useRef<HTMLButtonElement>(null);
  /*
    The active-filter chips count as inside too.

    They are the same cluster of controls as the panel — each one drops the filter it names — so
    treating them as "outside" meant clicking a chip with the panel open dismissed the panel and
    did nothing else, and you had to click the chip twice. Dropping a filter is not a click that
    wants the panel gone.
  */
  const filterChips = useRef<HTMLDivElement>(null);
  const closeFilters = useCallback(() => setFiltersOpen(false), []);
  // Swallowing the click, because this panel covers the list — see `useDismiss`.
  useDismiss(filtersOpen, closeFilters, [filterPanel, filterButton, filterChips], {
    swallowClick: true,
  });
  const goals = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });

  /*
    A search always looks in finished work too.

    The header search has always included done tasks and this box has not, so "did I already
    return that jacket?" got two different answers on the same screen. Retrieval is not filtering:
    when you are hunting for something specific, whether you already did it is the answer you came
    for, not a reason to hide it.
  */
  const searching = (filter.q ?? '') !== '';

  /*
    Scope rides on the filter rather than beside it, so it lands in the query key with everything
    else and a switch refetches once. Searching still widens to finished work; the two compose,
    which is what makes "Phase 2 that I have taken on" one question rather than two screens.
  */
  const scoped: TaskFilter = {
    ...filter,
    scope: scope.kind,
    ...(scope.kind === 'project' ? { projectId: scope.projectId } : {}),
  };
  const effective: TaskFilter = searching ? { ...scoped, includeDone: true } : scoped;

  // The same one-fetch trick the goal chip uses: names for the chips, and options for the filter.
  const projects = useQuery({ queryKey: ['projects'], queryFn: listProjects });

  /*
    A remembered project this account cannot reach falls back to `mine`, and the effect that
    writes the scope rewrites the stale key with it.

    The list must never sit on somebody else's project: the server answers that with an empty list
    rather than a refusal (it is filtering, not fetching one row), and an empty Tasks screen beside
    a full Today reads as lost work. Left, deleted and archived projects are the same case —
    `listProjects` is what the scope picker offers, so a project missing from it is one the picker
    could not show as selected either. The task query waits for that answer rather than firing the
    doomed request first; if the project list itself fails, it goes ahead as before.
  */
  const unreachable =
    scope.kind === 'project' &&
    projects.data !== undefined &&
    !projects.data.some((p) => p.id === scope.projectId);
  useEffect(() => {
    if (unreachable) setScope({ kind: 'mine' });
  }, [unreachable]);
  const scopeSettled =
    scope.kind !== 'project' || projects.isError || (projects.data !== undefined && !unreachable);
  /*
    The summary's own query — same key, so this is the cache `TaskOverview` filled rather than a
    second request — read here for the shelf's foot line. One count, so the chip above and the
    line below cannot disagree about how many are parked.
  */
  const overview = useQuery({
    queryKey: taskOverviewKey(scope),
    queryFn: () => getTaskOverview(scope),
  });
  const shelf = overview.data?.byStatus.parked ?? 0;
  const tasks = useQuery({
    queryKey: ['tasks', effective],
    queryFn: () => listTasks(effective),
    enabled: scopeSettled,
  });
  const allLabels = useQuery({ queryKey: ['labels'], queryFn: listLabels });
  const labelGroups = useQuery({ queryKey: ['label-groups'], queryFn: listLabelGroups });

  const goalPickerOptions = goalOptions(goals.data ?? []);
  /*
    The same tree already fetched for the filter, reused to name *and* colour each row's goal.

    Position in the tree gives every goal a distinct colour without anybody choosing one — no
    goal on either of the owner's accounts has ever had a colour set, so a scheme that started
    blank would have shown them exactly what they have today.
  */
  const colorByGoal = goalColorMap(goals.data ?? []);
  /*
    Where each row's colour comes from: itself, then its project, then its goal.

    Project beats goal because a task has at most one project and can serve several, and because
    every goal is auto-coloured already — a goal-wins rule would mean a project colour essentially
    never appeared. Resolved here on read, never stored, which is what makes a project recolour
    retint its tasks with no backfill.
  */
  /*
    Projects nest, so one set to Auto takes its nearest coloured ancestor rather than falling back
    to grey — the ancestor chain is the answer, not a fallback from it.
  */
  const projectById = new Map(
    (projects.data ?? []).map(
      (p) => [p.id, { id: p.id, color: p.color, parentId: p.parentProjectId }] as const,
    ),
  );
  const projectNames = new Map((projects.data ?? []).map((p) => [p.id, p.title] as const));
  /** Titles and parents, for the scope switcher's `Parent › Child` naming. */
  const projectsByIdForNames = new Map(
    (projects.data ?? []).map(
      (p) => [p.id, { title: p.title, parentProjectId: p.parentProjectId }] as const,
    ),
  );
  const fromProject = (projectId: string | null) =>
    nearestColored(
      projectId ? (projectById.get(projectId) ?? null) : null,
      projectById,
      (node) => projectNames.get(node.id) ?? 'its project',
    );
  const goalRawColor = new Map(
    flattenGoalNodes(goals.data ?? []).map((n) => [n.goal.id, n.goal.color] as const),
  );
  /*
    Labels come first, ahead of the project and the goal.

    One new entry in the list — which is what Phase 59's resolver was written generic for, and the
    reason the open labels question could not force a rebuild. The first coloured label wins; the
    order within a task follows the label list's own order, so it is stable rather than arbitrary.
  */
  const labelColors = new Map((allLabels.data ?? []).map((l) => [l.id, l] as const));
  const fromLabel = (ids: string[]) => {
    for (const id of ids) {
      const label = labelColors.get(id);
      if (label?.color) return { color: label.color, from: label.name };
    }
    return null;
  };

  const rowColor = (task: Task) =>
    resolveTaskColor(
      [
        { color: task.color, from: 'this task' },
        /*
          The parent, right behind the task's own choice.

          `resolveColor` asks each source at read time and never writes back, so putting the parent
          here gives subtasks live inheritance with no migration and nothing to go stale. It sits
          below an explicit colour on the subtask — that is a deliberate choice about this piece of
          work — and above the label, project and goal, which a subtask inherits from its parent
          anyway, so it only ever changes the answer when the parent was coloured by hand.
        */
        { color: task.parent?.color ?? null, from: 'its parent' },
        fromLabel(task.labelIds) ?? { color: null, from: '' },
        fromProject(task.projectId) ?? { color: null, from: '' },
        {
          color: task.goalIds[0] ? (goalRawColor.get(task.goalIds[0]) ?? null) : null,
          from: 'its goal',
        },
        // The goal's *assigned* class is the last word — see `resolveTaskColor`.
      ],
      task.goalIds[0] ? (colorByGoal.get(task.goalIds[0]) ?? null) : null,
    );

  /*
    A label's name, and the path that makes it mean something.

    The Labels screen renders nesting correctly; a task row printed the bare leaf, so two children
    of different parents were indistinguishable and the hierarchy the reader built existed in the
    data and vanished at the point of use. `VLAN cutover` becomes `Home lab rebuild › VLAN
    cutover`.

    No new sizing or overflow rule here, per the chip budget beside `.goal-chip`: the chip flexes
    and truncates as every other chip does, and the full path is in the `title` for when it cannot
    be shown whole.
  */
  const labelById = new Map((allLabels.data ?? []).map((l) => [l.id, l] as const));
  const labelPath = (id: string): string => {
    const parts: string[] = [];
    let node = labelById.get(id);
    // Bounded rather than trusting the data to be acyclic: a cycle here would hang the render.
    for (let depth = 0; node && depth < 8; depth += 1) {
      parts.unshift(node.name);
      node = node.parentLabelId ? labelById.get(node.parentLabelId) : undefined;
    }
    return parts.join(' › ');
  };
  const labelNames = new Map((allLabels.data ?? []).map((l) => [l.id, labelPath(l.id)] as const));
  /*
    Label options for the filter, each carrying the name of the group it answers.

    That subtitle is what makes picking unambiguous: "Phase 1" from the *Phase* group cannot be
    mistaken for "Phase 13", which is the confusion that made the text box useless — it searches
    descriptions too, so `Phase 1` matched `Phase 13`, `Phase 10`, and every task that merely
    mentioned phase 1.
  */
  // Whose group it is, not just its name — see `labelGroupCaption`.
  const groupNames = new Map(
    (labelGroups.data ?? []).map((g) => [g.id, labelGroupCaption(g, projects.data ?? [])] as const),
  );
  const filterLabelOptions = (allLabels.data ?? []).map((l) => ({
    value: l.id,
    label: l.name,
    data: { color: l.color, groupName: groupNames.get(l.groupId) ?? '' },
  }));

  /*
    What is being filtered, said outside the panel.

    A count in a button tells you that something is on and not what — so a list that looks wrong is
    indistinguishable from a filter you forgot. Each chip drops its own filter.
  */
  const clearLabelFilter = (id: string) =>
    setFilter((f) => {
      const rest = (f.labelIds ?? []).filter((x) => x !== id);
      return { ...f, labelIds: rest.length > 0 ? rest : undefined };
    });
  const activeChips: { key: string; label: string; clear: () => void }[] = [
    ...(filter.labelIds ?? []).map((id) => ({
      key: `label-${id}`,
      label: labelNames.get(id) ?? 'label',
      clear: () => clearLabelFilter(id),
    })),
    ...(filter.status
      ? [
          {
            key: 'status',
            label: String(filter.status).replace('_', ' '),
            clear: () => setFilter((f) => ({ ...f, status: undefined })),
          },
        ]
      : []),
    ...(filter.minPriority !== undefined
      ? [
          {
            key: 'priority',
            label: `${PRIORITY_LABELS[filter.minPriority] ?? filter.minPriority} and up`,
            clear: () => setFilter((f) => ({ ...f, minPriority: undefined })),
          },
        ]
      : []),
    ...(dueChoice
      ? [
          {
            key: 'due',
            label:
              /* *late*, the word the rows' pills, the count chip and Today's band all use —
                 `LateWork` dropped *overdue* for it, and this pill was the last to say it. The
                 Deadline control's option still reads "Overdue"; see `TILE_LABELS` on why. */
              dueChoice === 'overdue'
                ? 'late'
                : dueChoice === 'today'
                  ? 'due today'
                  : dueChoice === 'week'
                    ? 'due this week'
                    : 'no date',
            clear: () => setDueChoice(null),
          },
        ]
      : []),
    ...(filter.blocked
      ? [
          {
            key: 'blocked',
            label: 'blocked',
            clear: () => setFilter((f) => ({ ...f, blocked: undefined })),
          },
        ]
      : []),
    /*
      "unfiled", not "inbox", all the way down to the query parameter.

      This is the *no goal, no project, no parent* predicate, and the screen has a second,
      different thing called the inbox: the status, which the summary chip counts, the board
      column holds and the status filter above selects. The pill said "inbox only" over a list
      that was neither only nor the inbox, and the chip beside it printed the *status* count, so
      one word carried two predicates and the numbers under them disagreed.

      Phase 58 fixed the label and left the field, so this client sent `inbox=true` to draw a pill
      reading "unfiled" — the same word in two senses, one screen apart. Phase 76 renamed the
      contract, so the client now says `unfiled` from the checkbox to the wire.
    */
    ...(filter.unfiled
      ? [
          {
            key: 'unfiled',
            label: 'unfiled',
            clear: () => setFilter((f) => ({ ...f, unfiled: undefined })),
          },
        ]
      : []),
    /*
      The two the group offered and the row could not turn off.

      Both of these were counted by the toggle and had no chip, which is the mirror of the defect
      this row is being fixed for: a filter the control admits to and the row cannot clear. The test
      for whether a filter belongs here is whether the group offers a control for it — the goal
      `Select` and the `Show done` checkbox are both in the panel — and not whether it happens to be
      a field on `TaskFilter`. `projectId` is the counter-example and is deliberately absent: there
      is no project control any more (the comment where it used to be says why), and nothing assigns
      `filter.projectId` at all — a project arrives on `scoped`, which is the scope and not a filter.
    */
    ...(filter.goalId
      ? [
          {
            key: 'goal',
            label: goalPickerOptions.find((o) => o.value === filter.goalId)?.label ?? 'goal',
            clear: () => setFilter((f) => ({ ...f, goalId: undefined })),
          },
        ]
      : []),
    /*
      Read off `filter.includeDone` and not off the checkbox, which is `searching || …`.

      A live search forces finished work in regardless (`effective` below), so the box reads ticked
      and disabled while the field itself is unset. No chip appears then, and that is right: the
      reader did not switch it on and cannot switch it off, so a clear press there would be a
      control that does nothing.
    */
    ...(filter.includeDone
      ? [
          {
            key: 'done',
            label: 'done included',
            clear: () => setFilter((f) => ({ ...f, includeDone: undefined })),
          },
        ]
      : []),
  ];

  /*
    The number on the toggle is the number of chips it hides.

    This was a second, hand-kept list of filter keys, and the two disagreed about more than the
    `late` that was filed. The count named `projectId`, `goalId` and `includeDone`, which drew no
    chip; the row drew the due choice — `overdue` included — and one chip *per label*, which the
    count saw as one. So Overdue was on, the row said so, and the control said no filter was set.

    Deriving it from the row leaves one statement of what is on and one way to turn each thing off.
    Deriving it from `keyof TaskFilter` instead would have counted `includeSubgoals`, `labelMatch`,
    `waiting`, `overdue` and `scheduledDate` — modifiers and navigation state, not things switched
    on in the group — so that direction overcounts exactly as far as the literal list undercounted.

    Two labels now count as two. That is the deliberate consequence: the number says how many
    presses the row is holding, which is what a reader opening a shut group wants to know.

    Still everything except the search box, which lives outside the collapsed group and draws no
    chip — that property came from the list this replaces and is preserved by `activeChips` simply
    not having a `q` entry.
  */
  const activeFilters = activeChips.length;

  /*
    A press on one of the summary's counts, as a toggle — and `late` through the Deadline choice.

    This was `setFilter(next)`, the tile's filter written straight in. For every tile but one that
    is a field the pill row reads, so *31 next* drew a *next ×* pill. The late tile's field is
    `late`, which the row does not read: the Deadline choice owns it, and the effect above writes it
    from `dueChoice`. So *36 late* cut the list to 36 rows with no pill, a bare *Filter* and a chip
    that looked unpressed, and a second press left the 36 where they were (`01a15a31-188b`).

    So `late` sets the choice, exactly as Today's *See them* does through `readAim`, and the effect
    writes the field. Not the other way round — deriving the pill from `filter.late` as well as from
    `dueChoice` — because that leaves two writers of one field and the Deadline control showing
    nothing picked over a list it is filtering.

    Pressing a pressed count drops just that count's fields. Pressing another one replaces the
    filter, as it always has, and drops the Deadline choice with it: the replace used to wipe
    `filter.late` while leaving the choice set, so a *next* press after Today's band showed an
    *overdue* pill over rows it was no longer filtering.
  */
  const pressOverview = (next: TaskFilter) => {
    const { late, ...rest } = next;
    if (tileIsPressed(next, filter)) {
      if (late !== undefined) setDueChoice(null);
      setFilter((f) => {
        const out = { ...f };
        for (const key of Object.keys(rest)) delete out[key as keyof TaskFilter];
        return out;
      });
      return;
    }
    setDueChoice(late !== undefined ? 'overdue' : null);
    setFilter(rest);
  };

  // One menu for the screen, wherever the click landed — the list and the board share it.
  const menu = useTaskMenu();
  /** The last command, so the list can say what it did and offer the way back. */
  const readback = useCommandReadback();
  const { record, withdraw: withdrawCommand } = readback;
  const inProgress = useInProgressCount();
  /*
    What just happened, named — because the row leaves and takes the evidence with it.

    "Reopen" rather than "Undo": completing runs the recurring successor, the habit entry and the
    timeline block, and putting the status back does not reverse those. Claiming an undo that only
    half undoes is worse than naming what the control does.

    One function, called from the keyboard handler, the selection bar and the row's own box. It
    used to be written inline in the keydown path alone, which is why `x` named what it had done
    and the bar beside it — six one-click, unconfirmed, whole-selection verbs — said nothing at all.
    The state and the claim rule are `useCommandReadback`'s, shared with Today and the Board; this
    only finds the rows the ids name in this list's copy.

    A row's own controls record before the request, so a refusal turns the readback into the line
    saying so (`01a15e3c-61e3`) — `withdrawCommand`, matched on the press's claim rather than the
    row's id (`01a16009-e4e1`). The held gap needs no undoing: the row is still in the next id
    list, and that is what releases it.
  */
  const recordCommand = useCallback(
    (status: Task['status'], ids: readonly string[]): ReadbackClaim =>
      record(
        status,
        (tasks.data ?? []).filter((t) => ids.includes(t.id)),
      ),
    [tasks.data, record],
  );

  /*
    Right-clicking outside the selection collapses it, which is what every list in every file
    manager does — and what makes this menu's single-task verbs unambiguous. Right-clicking *inside*
    a selection leaves it alone, and the menu says it is only acting on the row you pointed at.
  */
  const openMenu = (event: React.MouseEvent, task: Task) => {
    if (selection.selectedIds.size > 0 && !selection.selectedIds.has(task.id)) selection.clear();
    menu.open(event, task);
  };

  const goalChips = new Map(
    flattenGoalNodes(goals.data ?? []).map((n) => [
      n.goal.id,
      { title: n.goal.title, colorClass: colorByGoal.get(n.goal.id) ?? '' },
    ]),
  );

  /*
    The cursor, and the keys that drive it.

    Memoised on the ids rather than on `tasks.data`: the query refetches on window focus and every
    ten seconds, and a fresh array identity each time would reconcile the cursor on a timer.
  */
  /*
    Subtasks under their parents, and the rule that decides which rows exist.

    Flat, a parent with five subtasks makes six rows saying overlapping things, and the whole job
    of the `K/N` pill is to let you not read them. The default view is the one you can act on
    without reading past things you did not ask for — the same argument the scope switcher settles
    for shared work.

    **The rule, stated for whatever is written against it next:** a child is a row like any other.
    The filter sees it, the query returns it, and it counts. Nesting is a display arrangement laid
    over the result set afterwards — so a child whose parent is *not* in the result renders at the
    top level rather than vanishing, and its parent link already names where it came from. No
    filter can ever hide a row by matching it.
  */
  /*
    Position expresses parentage; the toggle only decides how much starts open.

    The `showSubtasks` branch used to return the query order untouched, so a parent sat at row 4
    with its three children at rows 11–13 at identical indentation, each reprinting "↗ Draft the
    quarterly writeup" in full. The chip was doing work adjacency does for free — and doing it
    badly, clipping to "↗ Draft the quarterly w…" on all three so it disambiguated nothing.

    That is the case the parent chip was designed for and not this one: the chip is for surfaces
    where the parent is *not* on screen. Four rows up, it is the wrong tool. `TaskRow` already
    suppresses it when `nested`, which is why this is an ordering change rather than a chip change.

    One arrangement now, governed by `expanded` alone, so the expander works in both modes — with
    the toggle on it seeds open rather than removing the control, which is what made a subtree
    impossible to fold.
  */
  const ordered = useMemo(() => {
    const all = tasks.data ?? [];
    const present = new Set(all.map((t) => t.id));
    const children = new Map<string, Task[]>();
    for (const t of all) {
      if (t.parentTaskId && present.has(t.parentTaskId)) {
        children.set(t.parentTaskId, [...(children.get(t.parentTaskId) ?? []), t]);
      }
    }
    /*
      Subtasks read in the order they were written, on every surface.

      The list's default order ends `..., desc(priority), desc(createdAt)` — newest first, which is
      right for an inbox — and the subtask queries in the panel and the full view end
      `asc(sortOrder), asc(createdAt)`. With `sort_order` 0 and equal priority, as it is on almost
      every row, those are exact opposites: the UX reviewer found *Read it back out loud → Draft
      the middle section → Pull the numbers from the export* in one place and the reverse in the
      other, three sequential writing steps presented last-first.

      Sorted here rather than in the query because nesting is already a display arrangement laid
      over the result set — see the rule above — and a group of siblings is the one place where
      order carries meaning the top-level list's does not.
    */
    for (const list of children.values()) {
      list.sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));
    }
    const out: { task: Task; nested: boolean }[] = [];
    for (const t of all) {
      // A child whose parent is not in the result renders at the top level and keeps its chip —
      // no filter may hide a row by matching it.
      if (t.parentTaskId && present.has(t.parentTaskId)) continue;
      out.push({ task: t, nested: false });
      if (expanded.has(t.id)) {
        for (const c of children.get(t.id) ?? []) out.push({ task: c, nested: true });
      }
    }
    return out;
  }, [tasks.data, expanded]);

  /** Whether anything in view has a parent or children — see the control's own comment. */
  const hasSubtasksInView = (tasks.data ?? []).some(
    (t) => t.parentTaskId !== null || t.subtasks.total > 0,
  );

  /*
    The toggle seeds the open set rather than bypassing it.

    "Show subtasks as their own rows" now means "start with every parent open" — the rows still sit
    under their parents, and a parent can still be folded, which flat mode made impossible by
    removing the expander entirely.
  */
  useEffect(() => {
    const all = tasks.data ?? [];
    if (!showSubtasks) {
      setExpanded(new Set());
      return;
    }
    const present = new Set(all.map((t) => t.id));
    setExpanded(
      new Set(
        all.map((t) => t.parentTaskId).filter((id): id is string => id !== null && present.has(id)),
      ),
    );
  }, [showSubtasks, tasks.data]);

  const ids = useMemo(() => ordered.map((r) => r.task.id), [ordered]);
  const { myUserId, nameFor, canClaim } = useAssignees(tasks.data ?? []);
  /*
    Which question the rows answer, so a change of question does not read as rows disappearing.

    The same string the query is keyed on, deliberately: filter, sort and search all change it, and
    all three lost the selection the same way — the manager's twelve rows went for one tick of a
    checkbox, the eight the new filter still drew included. `expanded` is *not* in here, because
    folding a subtask group is a statement about those rows rather than about the list.
  */
  const listKey = JSON.stringify(effective);
  const selection = useTaskSelection(ids, listKey);
  /*
    Selected, and not on screen — the number the bar has to say out loud.

    Counted against what the filter returned rather than what is drawn: a subtask under a folded
    parent is not hidden *by the filter*, and its parent's chip already says it is there.
  */
  const hiddenCount = useMemo(() => {
    // Nothing fetched yet says nothing about what is hidden: reporting the whole selection as
    // hidden for the frame the new query is in flight is a flicker that reads as data loss.
    if (tasks.data === undefined) return 0;
    const shown = new Set(tasks.data.map((t) => t.id));
    return [...selection.selectedIds].filter((id) => !shown.has(id)).length;
  }, [tasks.data, selection.selectedIds]);
  /*
    Whether the selection already covers everything the filter matched.

    Against `ids` — what the filter returned — rather than the rows drawn, because `selectAll`
    writes exactly that set and a selection deliberately keeps rows the list is no longer drawing
    (`hiddenCount` above, pinned by `selectionSurvivesFilter.e2e.spec.ts`). The header box and the
    command bar's sentence both read this one value, so neither can claim "all" about a different
    set from the other.

    `ids.length > 0` is part of it: an empty list has not had everything selected, it has had
    nothing, and a box reading checked over no rows is the kind of true-by-vacuity this codebase
    keeps finding in its own tests.
  */
  const allMatchingSelected = ids.length > 0 && selection.selectedIds.size === ids.length;
  /*
    The age the pill becomes worth drawing at, for this list.

    Off `tasks.data` — what the filter returned — rather than the rows drawn, so folding a subtask
    group does not move the threshold. A narrowed list is a different question about a different
    set, and the pill should mark the outlier of the set somebody is actually looking at.
  */
  const ageThreshold = useMemo(
    () => staleThreshold((tasks.data ?? []).map((t) => t.createdAt)),
    [tasks.data],
  );
  /*
    Whatever the panel is showing is what the keys act on.

    This covers the title click the report names and every other way a row becomes the thing you
    are looking at — the parent arrow, the row menu's *Open*, `Enter`, a subtask followed from
    inside the panel, a task reached from global search — because they all end at the same
    `openTaskId`. A task with no row in this list parks the cursor rather than leaving it aimed
    somewhere else.
  */
  useCursorFollowsOpenTask(selection, ids, openTaskId);
  const command = useTaskCommand();

  /*
    Keep the cursor on screen.

    `j` walks the cursor down the list and nothing scrolled after it, so thirty presses left the
    cursor 731px below the fold with `scrollY` still at 0 — the command bar naming a task that was
    nowhere in view. `x` and `#` act on the cursor, so the app would happily complete or drop a row
    the user could not see.

    `block: 'nearest'` so a cursor already in view does not jog the page on every keystroke.
  */
  useEffect(() => {
    if (selection.cursorId === null) return;
    const row = document.querySelector(`[data-cursor]`);
    row?.scrollIntoView({ block: 'nearest' });
    /*
      And clear of the pinned dock, which the row's `scroll-margin-block` cannot know about.

      That margin clears the header. The dock under it is as tall as whatever it holds right now —
      nothing, the readback line, the bar, or both — so a fixed margin is either too small or wastes
      a band on every walk. With the readback up after an `x`, walking back up with `k` parked the
      cursor at 56px under a line ending at 79: the next `x` completing a row nobody could see,
      which is the hazard this effect exists for (`01a03813-a118`).
    */
    const dock = document.querySelector('.command-dock')?.getBoundingClientRect();
    const at = row?.getBoundingClientRect();
    if (dock && at && dock.height > 0 && at.top < dock.bottom) {
      window.scrollBy(0, at.top - dock.bottom - 4);
    }
  }, [selection.cursorId]);

  /*
    The list's keyboard belongs to the list.

    The board has its own cursor and its own bindings, and both were listening on `document` at
    once. Two consequences, one visible and one not: `x` and `1`/`2`/`3` acted on *this* cursor
    while the board was showing — a row nobody could see, which is the exact hazard the
    cursor-scrolling fix existed to remove — and, because keydown is a discrete event that React
    flushes synchronously, this handler's state change re-rendered the board mid-dispatch and
    tore its listener down before the same event reached it. Space selected nothing on the board
    and no amount of staring at the board's own handler explained why.

    `null` while the board is showing, so this screen is not listening *and* is not claiming the
    `tasks` group in the `?` panel — the board claims `board` instead. One call decides both.
  */
  useKeys(
    'tasks',
    view !== 'list'
      ? null
      : (e: KeyboardEvent) => {
          if (isTypingTarget()) return;
          // A focused bar or panel button answers its own Enter and Space — see `focusOwnsKey`.
          if (focusOwnsKey(e)) return;
          if (e.metaKey || e.ctrlKey || e.altKey) return;

          /*
        Shift walks the cursor and takes the rows with it.

        Before this the only way to build a batch was the mouse: `Shift-click` selected a span,
        `Space` on every row was the keyboard's answer at one keystroke per row, and `selectAll`
        was written, exported and bound to nothing.

        Tested before the plain `j`/`k` below, which would otherwise swallow them: with a modifier
        held `e.key` for the arrows is still `ArrowDown`/`ArrowUp`.

        And matched on `shiftKey` with either case of the letter rather than on `'J'` alone.
        `e.key` is the *character produced*, which is the layout's business: it is `'J'` on a US
        keyboard and need not be anywhere else, and a synthesised press can hold Shift while
        reporting `'j'`. Reading the modifier asks the question this binding actually means.
      */
          if (e.shiftKey && (e.key === 'J' || e.key === 'j' || e.key === 'ArrowDown')) {
            e.preventDefault();
            return selection.extendBy(1);
          }
          if (e.shiftKey && (e.key === 'K' || e.key === 'k' || e.key === 'ArrowUp')) {
            e.preventDefault();
            return selection.extendBy(-1);
          }
          if (e.key === 'j' || e.key === 'ArrowDown') {
            e.preventDefault();
            return selection.move(1);
          }
          if (e.key === 'k' || e.key === 'ArrowUp') {
            e.preventDefault();
            return selection.move(-1);
          }
          // Every row the filter is showing. `Ctrl-a` is the browser's, and the guard above has
          // already returned on it; this is the one the `?` sheet documents.
          if (e.key === 'a') {
            e.preventDefault();
            return selection.selectAll();
          }
          if (e.key === ' ') {
            if (selection.cursorId === null) return;
            e.preventDefault();
            return selection.toggle(selection.cursorId);
          }
          /*
        The subtask group under the cursor, opened and closed — the same two keys the goal tree
        binds to the same action.

        Nine subtasks across four collapsed parents were mouse-only, and the `?` panel explained
        why: on this screen `h`/`l` were captioned "Left/Right a column (board)", which is the
        board's meaning printed on a list. Arrows too, since the tree binds those as well.
      */
          if (e.key === 'l' || e.key === 'ArrowRight' || e.key === 'h' || e.key === 'ArrowLeft') {
            if (selection.cursorId === null) return;
            const open = e.key === 'l' || e.key === 'ArrowRight';
            const id = selection.cursorId;
            /* A row with no children has nothing to open, and swallowing the key there would make the
           binding feel broken on three rows out of four. */
            const hasChildren = (tasks.data ?? []).some((t) => t.parentTaskId === id);
            if (!hasChildren) return;
            e.preventDefault();
            return setExpanded((current) => {
              const next = new Set(current);
              if (open) next.add(id);
              else next.delete(id);
              return next;
            });
          }
          if (e.key === 'Escape') {
            /*
          Layered: the innermost open thing first, the selection only when nothing else is open.

          One press closed the bulk-edit popover *and* discarded the batch behind it — so backing
          out of a menu, the ordinary way to change your mind about opening one, threw away twelve
          rows that had taken twelve clicks to assemble, with nothing saying so and nothing
          restoring them. Same harm as losing a selection to a filter change, reached by a
          different gesture.

          Asked of `hasOpenSurface` rather than by ordering listeners: this handler registers when
          the list mounts and a popover registers when it opens, so this one always runs first and
          no amount of `stopPropagation` in the popover can get in front of it.
        */
            if (e.defaultPrevented || hasOpenSurface()) return;
            return selection.clear();
          }
          if (e.key === 'Enter') {
            if (selection.cursorId === null) return;
            e.preventDefault();
            // Shift-Enter reads it full-width; Enter alone keeps the panel, which is the default
            // everywhere else too.
            if (e.shiftKey && onOpenFull) return onOpenFull(selection.cursorId);
            return onOpen(selection.cursorId);
          }

          const hit = COMMANDS.find((c) => c.key === e.key);
          if (hit && selection.targets.length > 0) {
            e.preventDefault();
            const acted = selection.targets;
            /*
          What just happened, named — because the row leaves and takes the evidence with it.

          `x` completed the reviewer's probe task with no toast, no undo and nothing saying which
          task it was. "Reopen" rather than "Undo": completing runs the recurring successor, the
          habit entry and the timeline block, and putting the status back does not reverse those.
          Claiming an undo that only half undoes is worse than naming what the control does.
        */
            recordCommand(hit.status, acted);
            command.mutate({ ids: acted, status: hit.status });
            /*
          The cursor parks in the gap rather than inheriting the next row — see `holdGap`.

          One press of `x` completed the reviewer's probe task and slid the cursor down; a second
          press, the natural undo instinct, completed a different task and unblocked a third. One
          keystroke moved three counters.
        */
            if (selection.selectedIds.size === 0) selection.holdGap();
          }
        },
  );

  return (
    <section>
      {/* The shape of the work, before the list of it — and the counts filter the list. */}
      {/* The scope, not just the filter: the chips are counted over the list they sit above. */}
      <TaskOverview onFilter={pressOverview} active={filter} scope={scope} />

      {/*
        The shared capture box. This screen had the only one in the app; it now has the same one
        as the header, so the date chip, the duplicate notice and the `c` key work identically
        wherever you are — and the two paths cannot drift into creating different things again.

        Not rendered at phone width, where the header shows its `+` on this screen instead. Hiding
        it with CSS would have left two "Capture a task" fields in the document — one of them
        invisible — which is the duplicate this arrangement exists to prevent, just moved somewhere
        harder to notice. `App.tsx` keys the header's disclosure on the same `PHONE` constant.
      */}
      {!phone && (
        <Capture
          project={
            scope.kind === 'project'
              ? { id: scope.projectId, title: projectNames.get(scope.projectId) ?? 'this project' }
              : null
          }
        />
      )}

      <div className="filter-head">
        <input
          type="search"
          placeholder="Search title and description"
          defaultValue={filter.q ?? ''}
          onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value || undefined }))}
        />
        <button
          type="button"
          ref={filterButton}
          className={`filter-toggle ${activeFilters > 0 ? 'is-active' : ''}`}
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((v) => !v)}
        >
          Filter{activeFilters > 0 ? ` (${activeFilters})` : ''}
        </button>
        {activeFilters > 0 && (
          <button
            type="button"
            className="link"
            onClick={() => {
              setDueChoice(null);
              setFilter((f) => ({ ...(f.q ? { q: f.q } : {}) }));
            }}
          >
            clear
          </button>
        )}

        {/*
          The way into the command bar, at every width.

          It arrived for the phone: at 375 every path into bulk triage — j/k, Space, shift-click —
          needs a physical keyboard, so the whole feature was desktop-only. It was then gated
          `phone &&`, with a comment saying why it belongs on a phone and nothing saying why it
          should be absent anywhere else.

          The gate's defence lived in a test instead, and it was wrong where it was specific:
          "Space, shift-click and ctrl-click all work here; a fourth way is a fourth thing to
          explain". They do all work — ctrl-click is handled on mousedown just above, and measuring
          it at 1280 selects rows and opens the bar, so the finding's claim that it "selected
          nothing" is false. But *working* and *being findable* are different properties, and a DOM
          sweep of every button and link on a list narrowed to ten blocked rows returned only the
          task titles. The whole of the affordance at this width was an 11px hint strip reading
          "Space select…".

          So this is not a fourth way to do it. It is the first one a reader who does not already
          know the conventions can see — and the argument the rest of this comment makes, that it
          names its state rather than toggling silently and that leaving clears what it gathered,
          never had anything to do with the width.
        */}
        <button
          type="button"
          className={`filter-toggle ${selecting ? 'is-active' : ''}`}
          aria-pressed={selecting}
          data-testid="select-mode"
          onClick={() => {
            const next = !selecting;
            setSelecting(next);
            // Leaving the mode clears what it gathered, so the bar cannot outlive it.
            if (!next) selection.clear();
          }}
        >
          {/*
              "Cancel", not "Done".

              Both words were on the screen at once, about 150px apart on a 375px viewport and with
              a live selection between them: this one left the mode, and the command bar's "Done"
              completed every selected task. Two labels, one word, opposite consequences, and the
              destructive one is the one you did not mean.

              The command bar keeps the word — completion is what the rest of the app calls "Done",
              on the board column, the status menu and the goal tree. And "Cancel" is the honest
              name for this control rather than a euphemism for it: leaving the mode clears the
              selection, which is the line directly below.
            */}
          {selecting ? 'Cancel' : 'Select'}
        </button>

        {/*
          Sort, beside Filter.

          There was none: order was fixed at `sortOrder, priority desc, createdAt desc`, and a
          deadline was not even a tiebreaker — so with 56 open tasks there was no way to bring the
          late ones to the top, which is the whole of triage.
        */}
        <Select
          compact
          options={[
            { value: 'due', label: 'Due date' },
            { value: 'priority', label: 'Priority' },
            { value: 'newest', label: 'Recently added' },
            { value: 'oldest', label: 'Oldest' },
          ]}
          value={filter.sort && filter.sort !== 'default' ? filter.sort : null}
          onChange={(next) => {
            // Remembered, and clearable: "Default order" writes the default back rather than
            // leaving the last choice to be restored on the next load.
            localStorage.setItem(SORT_KEY, next ?? 'default');
            setFilter((f) => ({ ...f, sort: (next as TaskFilter['sort']) ?? undefined }));
          }}
          placeholder="Sort"
          emptyLabel="Default order"
          ariaLabel="Sort tasks"
        />

        {/*
          What this list means, beside the controls that shape it.

          Scope has existed on the API since Phase 1 with nothing to set it, so the screen has been
          showing `mine` and calling it "Tasks". Naming it is half the value: a list you cannot see
          the scope of is a list you have to guess about, and "everything reachable" and "mine" look
          identical until somebody shares a project with you.

          A `Select` rather than a third segmented control: the projects make it an open-ended list,
          and two segmented groups plus a sort box on one line is where a band starts wrapping.
        */}
        {(projects.data?.length ?? 0) > 0 && (
          <Select
            compact
            options={[
              { value: 'mine', label: 'My tasks' },
              { value: 'all', label: 'Everything' },
              /*
                Ordered by the materialised path, so a child follows its own parent. Unsorted, the
                depth-3 project sat four unrelated projects below the one it belongs to — which is
                the surprise the `Parent › Child` prefix exists to remove, and it cannot remove it
                on its own.
              */
              ...[...(projects.data ?? [])]
                .sort((a, b) => a.path.localeCompare(b.path))
                .map((p) => ({
                  value: `project:${p.id}`,
                  label: prefixedName(p, projectsByIdForNames),
                })),
            ]}
            value={scope.kind === 'project' ? `project:${scope.projectId}` : scope.kind}
            onChange={(next) => {
              const picked = next ?? 'mine';
              setScope(
                picked.startsWith('project:')
                  ? { kind: 'project', projectId: picked.slice(8) }
                  : picked === 'all'
                    ? { kind: 'all' }
                    : { kind: 'mine' },
              );
            }}
            placeholder="My tasks"
            /*
              Scope always has a value, so there is no "none" to offer — and offering it printed
              "My tasks" twice, once as the empty choice wearing the placeholder and once as the
              real option, one directly above the other with the second marked active.
            */
            allowEmpty={false}
            ariaLabel="Scope"
          />
        )}

        {/*
          The list/board toggle, on the same line as the search.

          It had a row of its own, which cost 44 pixels to say something that fits beside the box it
          modifies — and every row above the first task is a row you scroll past to reach your work.
        */}
        <div className="view-toggle" role="group" aria-label="View">
          <button
            type="button"
            className={view === 'list' ? 'is-active' : ''}
            aria-pressed={view === 'list'}
            onClick={() => setView('list')}
          >
            List
          </button>
          <button
            type="button"
            className={view === 'board' ? 'is-active' : ''}
            aria-pressed={view === 'board'}
            onClick={() => setView('board')}
          >
            Board
          </button>
        </div>

        {/*
          One control, both views.

          Board and list disagreed about what a subtask *is*: the list folds one under its parent
          behind an expander, the board drew a card for it regardless — so the same account showed
          a different number of cards than rows, with nothing on either surface saying why. The
          reviewer found the mismatch; the architect found the same seam from the other side, in a
          board that kept a selection model but drew none of the nesting.

          Three answers were defensible (cards for subtasks always, parents only, or one toggle for
          both) and this is the third. A column is a status and a subtask has its own, so the honest
          objection to it is that a folded board hides work that is genuinely in a column — but the
          *list* already hides exactly that work behind the same expander, and this checkbox is the
          control that exists to reveal it. Better one idea with one switch than two views quietly
          holding different opinions.

          The label names what the view actually draws, because "rows" on a board is a lie.
        */}
        {/*
          Only where there is something to expand.

          It occupied a full line between the filter bar and the list and, on an account with no
          subtasks at all, changed nothing — 58 rows either way. A permanent control that is a
          no-op for most accounts is exactly the tax the minimalist objects to.

          The wording follows the behaviour: children now sit under their parents either way, so
          the choice is whether they start open, not whether they exist as rows.
        */}
        {hasSubtasksInView && (
          <label className="subtext show-subtasks">
            <input
              type="checkbox"
              checked={showSubtasks}
              onChange={(e) => setShowSubtasks(e.target.checked)}
            />{' '}
            Open every parent
          </label>
        )}

        {/*
          Anchored to the toolbar, so opening it floats over the list instead of pushing it down.

          Six controls in a band that stays open meant narrowing the list showed *less* of it —
          with an overdue filter applied and 25 matches, the first row sat at y=737 of 900. The
          active filters are already summarised as chips below, so this is a place to change them
          rather than the place they live.
        */}
        <div className="filters card" ref={filterPanel} hidden={!filtersOpen}>
          <select
            value={filter.status ?? ''}
            onChange={(e) =>
              setFilter((f) => ({ ...f, status: (e.target.value || undefined) as never }))
            }
          >
            <option value="">Any status</option>
            {/* Blocked is not a status here or anywhere: it is the checkbox below, read from the
                derived state every other screen uses. */}
            {/* `parked` is here and nowhere else by default: the whole of what parking does is
                keep a task out of the unfiltered list, so this select is the way back to it. */}
            {(
              ['inbox', 'next', 'in_progress', 'parked', 'done', 'dropped'] satisfies TaskStatus[]
            ).map((s) => (
              <option key={s} value={s}>
                {s.replace('_', ' ')}
              </option>
            ))}
          </select>
          {/*
            Filter by label — the replacement the comment below promised and never delivered.

            Projects were retired in favour of labels, and the note said labels replace them. They
            could not: nothing in the service or the route filtered by label, so the only way to ask
            for "Phase 1" was the text box, which searches descriptions as well as titles. In a
            backlog whose cards cross-reference each other it returned Phase 13, Phase 10, and
            everything that mentioned phase 1 in passing.

            Each option carries its group name, so "Phase 1" from the *Phase* group is unambiguous.
            Several labels mean *all* of them, because across groups the question is "Phase 1 and
            bug" rather than "either".
          */}
          {/*
            A control that can do nothing is not shown.

            On an account with no labels this opened an empty dropdown reading `Nothing matches ""`
            — with bare empty quote marks — and covered the row beneath it. The same rule below for
            goals, where the list held exactly one option: its own current value.
          */}
          {filterLabelOptions.length > 0 && (
            <MultiSelect
              options={filterLabelOptions}
              value={filter.labelIds ?? []}
              onChange={(next) =>
                setFilter((f) => ({ ...f, labelIds: next.length > 0 ? next : undefined }))
              }
              placeholder="Any label"
              ariaLabel="Filter by label"
              dialogTitle="Labels to filter by"
              renderOption={(option) => (
                <>
                  <span
                    className={`swatch ${option.data?.color ? `goal-c-${option.data.color}` : ''}`}
                  />
                  <span className="t">{option.label}</span>
                  <span className="sub">{option.data?.groupName}</span>
                </>
              )}
            />
          )}
          {/* Priority and deadline: the `10 blocked` chip proved that one click onto a real subset is
              the best thing on this screen, and these are the two subsets triage asks for next. */}
          <Select
            options={[
              { value: '3', label: 'High only' },
              { value: '2', label: 'Medium and up' },
              { value: '1', label: 'Low and up' },
            ]}
            value={filter.minPriority === undefined ? null : String(filter.minPriority)}
            onChange={(next) =>
              setFilter((f) => ({ ...f, minPriority: next ? Number(next) : undefined }))
            }
            placeholder="Any priority"
            emptyLabel="Any priority"
            ariaLabel="Filter by priority"
          />
          <Select
            options={[
              { value: 'overdue', label: 'Overdue' },
              { value: 'today', label: 'Due today' },
              { value: 'week', label: 'Due this week' },
              { value: 'none', label: 'No date' },
            ]}
            value={dueChoice}
            onChange={(next) => setDueChoice(next)}
            placeholder="Any deadline"
            emptyLabel="Any deadline"
            ariaLabel="Filter by deadline"
          />
          {/* The project filter is gone with the Projects screen — labels replace it, and a filter
              for a concept with no home is a dead end. */}
          {goalPickerOptions.length > 0 && (
            <Select
              options={goalPickerOptions}
              value={filter.goalId ?? null}
              onChange={(next) => setFilter((f) => ({ ...f, goalId: next ?? undefined }))}
              placeholder="Any goal"
              emptyLabel="Any goal"
              ariaLabel="Filter by goal"
              renderOption={renderGoalOption}
            />
          )}
          {/* Named for what it selects, not for the status of the same name — see the `unfiled`
              pill above. The title carries the definition, because "unfiled" is a shorter word
              than the rule behind it. */}
          <label className="check" title="No goal and no project — capture nobody has filed yet">
            <input
              type="checkbox"
              checked={filter.unfiled ?? false}
              onChange={(e) => setFilter((f) => ({ ...f, unfiled: e.target.checked || undefined }))}
            />
            Unfiled
          </label>
          {/* "Blocked", not "waiting on something" — see `BlockedBanner.tsx` for why the word
              moved. The query parameter is `blocked` and always was, so this is a label change
              only; nothing published changes name. */}
          <label className="check" title="Held up by an open blocker task — not startable yet">
            <input
              type="checkbox"
              checked={filter.blocked === true}
              onChange={(e) => setFilter((f) => ({ ...f, blocked: e.target.checked || undefined }))}
            />
            Blocked
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={searching || (filter.includeDone ?? false)}
              disabled={searching}
              title={searching ? 'A search always includes finished work' : undefined}
              onChange={(e) =>
                setFilter((f) => ({ ...f, includeDone: e.target.checked || undefined }))
              }
            />
            Show done
          </label>
        </div>
      </div>

      {/*
        The flat view, for the question that wants it.

        Beside the list rather than inside the filter panel, because it does not change *which*
        tasks are shown — every subtask is in the result set either way — only whether they are
        drawn under their parents. A control in the filter row would read as one more thing
        narrowing the list, which is the mistake capture's defaults row was just moved for.
      */}

      {activeChips.length > 0 && (
        <div className="active-filters" ref={filterChips}>
          {activeChips.map((c) => (
            <button
              key={c.key}
              type="button"
              className="pill active-filter"
              onClick={c.clear}
              title={`Stop filtering by ${c.label}`}
            >
              {c.label} ×
            </button>
          ))}
        </div>
      )}

      {view === 'board' ? (
        <TaskBoard
          /*
            `scoped`, not `filter`.

            The scope switcher above is shared by both views, and the list's own query uses
            `effective` — `filter` merged with the scope and the project id. The board was handed
            the raw prop, so the selection never reached its query and the server fell back to
            `scope: 'mine'`: picking a project re-filtered the list and left the board showing the
            viewer's own tasks across every project.

            `scoped` rather than `effective` because `includeDone` is the *search* box's business,
            and the board has a Done column of its own.
          */
          filter={scoped}
          showSubtasks={showSubtasks}
          goalChips={goalChips}
          colorOf={rowColor}
          labelNames={labelNames}
          onOpen={onOpen}
          onMenu={openMenu}
          openTaskId={openTaskId}
        />
      ) : (
        <>
          <QueryState query={tasks} />
          {tasks.data?.length === 0 && (
            <>
              <p className="muted">
                {filter.q
                  ? 'Nothing matches that search.'
                  : 'Nothing here. Capture something above.'}
              </p>
              {/* Not while searching: an empty result for `csv` is a fact about the search box,
                  and answering it with "here are your projects" would be a non sequitur. */}
              {!filter.q && (
                <WorkElsewhere
                  onOpenBoard={(projectId) => {
                    setScope({ kind: 'project', projectId });
                    setView('board');
                  }}
                />
              )}
            </>
          )}

          {/*
            The bar and the readback, pinned together under the header — see `ReadbackSlot`. One
            dock so the two stack instead of both claiming `top: var(--header-height)`.
          */}
          <div className="command-dock">
            {/*
            Only when something is actually selected.

            It rendered whenever the list was non-empty and acted on the *cursor*, so it printed a
            copy of the first row directly above the first row — the minimalist's complaint — while
            the manager, who lives on it, found it unreachable past the first screenful and absent
            from the board entirely. Three personas, five findings, wanting opposite things: the
            sign of a control encoding fewer states than the data has.

            One control, two states. Nothing selected and there is no bar, so the list is the list.
            Something selected and the verbs appear, on both views, with the count. The keyboard
            bindings that act on the cursor are unaffected — they were never the bar.
          */}
            {selection.selectedIds.size > 0 && (
              <TaskCommandBar
                targets={selection.targets}
                selectedCount={selection.selectedIds.size}
                hiddenCount={hiddenCount}
                /*
                Named from the filter chips, which are already the screen's own words for what it
                is showing — "blocked", "overdue", "no date". Only when the selection really is the
                whole matched set *and* something is narrowing it: "All 56 selected" over an
                unfiltered list is a longer way of saying 56.
              */
                allOf={
                  allMatchingSelected && activeChips.length > 0
                    ? activeChips.map((c) => c.label).join(', ')
                    : null
                }
                onClearSelection={selection.clear}
                onCommitted={(status) => recordCommand(status, selection.targets)}
                onDescribeEdit={(what, count) =>
                  readback.show({
                    /*
                    Named, and honest about the way back.

                    Apply rewrites dates, labels, goals and the assignee, so a "Reopen" here would
                    claim an undo that only restores status — the exact thing the note on
                    `recordCommand` warns against. An empty `from` means the readback names what
                    happened and offers no button it cannot honour.
                  */
                    verb: `Changed ${what} on`,
                    landed: null,
                    titles:
                      count === 1
                        ? (tasks.data ?? []).find((t) => t.id === selection.targets[0])?.title
                          ? [(tasks.data ?? []).find((t) => t.id === selection.targets[0])!.title]
                          : ['1 task']
                        : new Array(count).fill(''),
                    from: [],
                  })
                }
                task={soleSelectedTask(selection.selectedIds, tasks.data)}
                projectId={scope.kind === 'project' ? scope.projectId : null}
              />
            )}

            {/*
              What the last command did, and the way back.

              The row leaves and takes the evidence with it: `x` completed the reviewer's probe task
              with no toast, no undo and nothing naming what had gone. "Reopen" rather than "Undo"
              because that is what the control does — completing runs the recurring successor, the
              habit entry and the timeline block, and putting the status back does not reverse them.

              The tail is how many that makes, after a start — the Board's figure, from the Board's
              query. Starting is the one verb here that adds to a pile somebody can be warned about,
              and this screen prints none of the Board's columns: in progress went 11 → 12 in silence
              (`01a15a31-174b`). The account's pile, deliberately not this filtered list's. Today's
              readback has no such clause: the WIP sentence sits right above it there.

              A bulk edit carries no `from`, so it gets the sentence and no button: Apply rewrites
              dates, labels, goals and the assignee, and a button would restore status alone.
            */}
            <ReadbackSlot
              readback={readback}
              tail={
                readback.line?.landed === 'in_progress' && inProgress !== null
                  ? ` — ${inProgress} in progress`
                  : undefined
              }
            />
          </div>

          {/*
            Above the list, not below it.

            This sat at document top 2921 of a 3095px page — below every row it describes — so the
            manager found multi-select by reading the source rather than the screen, and reported
            that nothing on Tasks says bulk triage exists. Once found it is fast: 13 keystrokes
            moved six tasks in under a second. The cost was entirely discovery.

            Selection leads, because that is the part nobody found. It is the same one line it was;
            it just sits where it can be read.
          */}
          {(tasks.data?.length ?? 0) > 0 && (
            /*
              Rendered from `keys.ts`, not restated.

              This line and the `?` panel were two documents: the footer advertised eight keys the
              panel never mentioned, and neither mentioned shift-click — which the manager found
              by guessing and called the best flow in the app.
            */
            <p className="subtext keys-hint">
              {inlineKeys('tasks').map(([key, what], i) => (
                <span key={key}>
                  {i > 0 && ' · '}
                  <kbd>{key}</kbd> {what.toLowerCase()}
                </span>
              ))}
            </p>
          )}

          {/*
            Select everything the filter is currently showing, by mouse.

            `a` has been bound to this since b322421, so the keyboard half was already answered and
            the mouse half was not: a DOM sweep for anything meaning *all* on a list narrowed to
            ten blocked rows returned only the task titles. Ctrl-click selected nothing.

            Only in select mode, because that is the mode this is about — a checkbox above an
            ordinary list would be a control for a state the reader has not asked to be in, and
            the toggle beside Filter is what asks.

            Checked reads `selectedIds.size === ids.length`, the same `ids` `selectAll` writes and
            `selectionSurvivesFilter` pins across a filter change, so the box cannot disagree with
            what the bar below is about to act on — including the rows the filter is hiding.
          */}
          {selecting && ordered.length > 0 && (
            <label className="select-all">
              <input
                type="checkbox"
                data-testid="select-all"
                checked={allMatchingSelected}
                onChange={() => (allMatchingSelected ? selection.clear() : selection.selectAll())}
              />
              <span>{allMatchingSelected ? 'None' : 'All'}</span>
            </label>
          )}

          <ul className="task-list">
            {ordered.map(({ task, nested }) => (
              <TaskRow
                blockersAreContent={blockersAreTheContent(filter)}
                ageThreshold={ageThreshold}
                key={task.id}
                task={task}
                nested={nested}
                expanded={expanded.has(task.id)}
                onToggleExpand={
                  task.subtasks.total > 0
                    ? () =>
                        setExpanded((prev) => {
                          const next = new Set(prev);
                          if (next.has(task.id)) next.delete(task.id);
                          else next.add(task.id);
                          return next;
                        })
                    : undefined
                }
                onOpen={onOpen}
                goalChips={goalChips}
                labelNames={labelNames}
                color={rowColor(task)}
                isCursor={task.id === selection.cursorId}
                isSelected={selection.selectedIds.has(task.id)}
                selecting={selecting}
                onFocusRow={() => selection.moveTo(task.id)}
                onToggleSelect={() => selection.toggle(task.id)}
                onMenu={openMenu}
                myUserId={myUserId}
                nameFor={nameFor}
                canClaim={canClaim}
                onExtendSelect={() => selection.extendTo(task.id)}
                /*
                  Armed with the row's own id, not the cursor's.

                  The same press moves the cursor here and acts on the row, and `holdGap()` reading
                  its own closure would arm the row the cursor was on *before* the press.

                  And recorded, so the row's box and the `x` key leave the same readback. Before
                  this only the keyboard did: the box's press completed a row and the row simply
                  went, which is survivable when completing costs two deliberate presses and is not
                  once it costs one — the toast *is* the undo the long press used to be, and unlike
                  a long press it is visible, reachable and names the task.
                */
                onAct={(status) => {
                  selection.holdGap(task.id);
                  return recordCommand(status, [task.id]);
                }}
                onFail={withdrawCommand}
              />
            ))}
          </ul>

          {/*
            Where the rest went, said once and quietly.

            The dormancy job parked 21 of the minimalist's 22 open tasks overnight and this list
            read "1 inbox" with nothing on the screen admitting the other 21 existed — "my list went
            from 22 things to 1" (`01a1774e-16c4`). Hiding the rows is the feature; hiding the
            number made it read as loss.

            Only over the default view — no filter, no search, no deadline choice — because only
            there is `byStatus.parked` exactly the set this list left out: the count is the scope's,
            and under a label filter "21 parked" would be a claim about rows that are not this
            list's. The chip in the summary still says it in those states. Absent at zero, and one
            muted line rather than a banner: both ends of the density scale asked for exactly that.
          */}
          {tasks.data !== undefined &&
            shelf > 0 &&
            !filter.status &&
            !filter.includeDone &&
            !searching &&
            activeFilters === 0 &&
            dueChoice === null &&
            overview.data && (
              <p className="subtext shelf-foot" data-testid="parked-foot">
                {shelf} parked ·{' '}
                <button
                  type="button"
                  className="link"
                  title="Show the parked tasks"
                  onClick={() => pressOverview(overview.data.filters.parked)}
                >
                  show
                </button>
              </p>
            )}
        </>
      )}

      <TaskMenu
        target={menu.target}
        onClose={menu.close}
        onOpenTask={onOpen}
        onOpenFull={onOpenFull}
        selectedCount={selection.selectedIds.size}
        // Every verb in this menu writes the row it was opened on, and three of them can take it
        // out of the list. Same bargain as the checkbox above.
        onAct={(id) => selection.holdGap(id)}
      />
    </section>
  );
}
